// NFC State Manager - Professional State Management for NFC Operations
import nfcConfigManager from './nfcConfigManager.js';
import nfcSecurityManager from './nfcSecurityManager.js';

// NFC States
export const NFC_STATES = {
  IDLE: 'idle',
  CHECKING_SUPPORT: 'checking_support',
  REQUESTING_PERMISSION: 'requesting_permission',
  INITIALIZING_SCAN: 'initializing_scan',
  SCANNING: 'scanning',
  CHIP_DETECTED: 'chip_detected',
  VALIDATING_CHIP: 'validating_chip',
  PROCESSING_ACTIVATION: 'processing_activation',
  SUCCESS: 'success',
  ERROR: 'error',
  TIMEOUT: 'timeout',
  CANCELLED: 'cancelled'
};

// Error Types
export const NFC_ERRORS = {
  NOT_SUPPORTED: 'not_supported',
  PERMISSION_DENIED: 'permission_denied',
  SCAN_FAILED: 'scan_failed',
  INVALID_CHIP: 'invalid_chip',
  ALREADY_CLAIMED: 'already_claimed',
  COOLDOWN_ACTIVE: 'cooldown_active',
  NETWORK_ERROR: 'network_error',
  SERVER_ERROR: 'server_error',
  TIMEOUT: 'timeout',
  USER_CANCELLED: 'user_cancelled',
  RATE_LIMITED: 'rate_limited',
  UNKNOWN_ERROR: 'unknown_error'
};

class NFCStateManager {
  constructor() {
    this.state = NFC_STATES.IDLE;
    this.error = null;
    this.data = null;
    this.listeners = new Set();
    this.scanController = null;
    this.rateLimitData = this._loadRateLimitData();
  }

  // Subscribe to state changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Emit state change to all listeners
  _emit() {
    const stateData = {
      state: this.state,
      error: this.error,
      data: this.data,
      timestamp: Date.now()
    };

    console.log('📡 [NFC State]', stateData);

    this.listeners.forEach(listener => {
      try {
        listener(stateData);
      } catch (error) {
        console.error('Error in NFC state listener:', error);
      }
    });
  }

  // Set state
  setState(newState, data = null, error = null) {
    this.state = newState;
    this.data = data;
    this.error = error;
    this._emit();
  }

  // Set error state
  setError(errorType, message, originalError = null) {
    this.setState(NFC_STATES.ERROR, { errorType, originalError }, message);
  }

  // Get current state
  getState() {
    return {
      state: this.state,
      error: this.error,
      data: this.data
    };
  }

  // Check if operation is in progress
  isOperationInProgress() {
    return ![
      NFC_STATES.IDLE,
      NFC_STATES.SUCCESS,
      NFC_STATES.ERROR,
      NFC_STATES.TIMEOUT,
      NFC_STATES.CANCELLED
    ].includes(this.state);
  }

  // Reset to idle state
  reset() {
    if (this.scanController) {
      this.scanController.abort();
      this.scanController = null;
    }
    this.setState(NFC_STATES.IDLE);
  }

  // Cancel current operation
  cancel() {
    if (this.scanController) {
      this.scanController.abort();
      this.scanController = null;
    }
    this.setState(NFC_STATES.CANCELLED);
  }

  // Load rate limiting data from localStorage
  _loadRateLimitData() {
    try {
      const stored = localStorage.getItem('nfc_rate_limit');
      return stored ? JSON.parse(stored) : { attempts: [], blocks: [] };
    } catch {
      return { attempts: [], blocks: [] };
    }
  }

  // Save rate limiting data to localStorage
  _saveRateLimitData() {
    try {
      localStorage.setItem('nfc_rate_limit', JSON.stringify(this.rateLimitData));
    } catch (error) {
      console.warn('Failed to save rate limit data:', error);
    }
  }

  // Check rate limiting
  async _checkRateLimit() {
    const config = await nfcConfigManager.getRateLimitConfig();
    const now = Date.now();

    // Clean old attempts (older than 1 hour)
    this.rateLimitData.attempts = this.rateLimitData.attempts.filter(
      timestamp => now - timestamp < 60 * 60 * 1000
    );

    // Clean old blocks
    this.rateLimitData.blocks = this.rateLimitData.blocks.filter(
      block => now - block.timestamp < block.duration
    );

    // Check if currently blocked
    const activeBlock = this.rateLimitData.blocks.find(
      block => now - block.timestamp < block.duration
    );

    if (activeBlock) {
      const remainingTime = Math.ceil((activeBlock.duration - (now - activeBlock.timestamp)) / 1000);
      throw new Error(`Rate limited. Try again in ${remainingTime} seconds.`);
    }

    // Check attempts in last minute
    const recentAttempts = this.rateLimitData.attempts.filter(
      timestamp => now - timestamp < 60 * 1000
    );

    if (recentAttempts.length >= config.maxAttemptsPerMinute) {
      // Add block
      this.rateLimitData.blocks.push({
        timestamp: now,
        duration: config.blockDurationMinutes * 60 * 1000
      });
      this._saveRateLimitData();
      throw new Error(`Too many attempts. Blocked for ${config.blockDurationMinutes} minutes.`);
    }

    // Check attempts in last hour
    if (this.rateLimitData.attempts.length >= config.maxAttemptsPerHour) {
      // Add longer block
      this.rateLimitData.blocks.push({
        timestamp: now,
        duration: 60 * 60 * 1000 // 1 hour
      });
      this._saveRateLimitData();
      throw new Error('Too many attempts today. Blocked for 1 hour.');
    }

    // Record this attempt
    this.rateLimitData.attempts.push(now);
    this._saveRateLimitData();
  }

  // Start NFC scan operation
  async startScan() {
    try {
      // Reset previous state
      this.reset();

      // Check rate limiting
      this.setState(NFC_STATES.CHECKING_SUPPORT);
      await this._checkRateLimit();

      // Check NFC support
      if (!('NDEFReader' in window)) {
        this.setError(
          NFC_ERRORS.NOT_SUPPORTED,
          await nfcConfigManager.getText('notSupported')
        );
        return false;
      }

      // Request permission
      this.setState(NFC_STATES.REQUESTING_PERMISSION);

      try {
        // Initialize scan
        this.setState(NFC_STATES.INITIALIZING_SCAN);

        const ndef = new NDEFReader();
        this.scanController = new AbortController();

        await ndef.scan({ signal: this.scanController.signal });

        // Start scanning
        this.setState(NFC_STATES.SCANNING, {
          instructions: await nfcConfigManager.getText('scanInstructions')
        });

        // Set timeout
        const timeout = await nfcConfigManager.getScanTimeout();
        const timeoutId = setTimeout(() => {
          if (this.state === NFC_STATES.SCANNING) {
            this.setState(NFC_STATES.TIMEOUT);
          }
        }, timeout);

        // Setup event listeners
        const readingHandler = async (event) => {
          clearTimeout(timeoutId);

          try {
            const { message, serialNumber } = event;

            this.setState(NFC_STATES.CHIP_DETECTED, {
              serialNumber,
              message: await nfcConfigManager.getText('detected')
            });

            // Validate chip
            await this._validateChip(serialNumber);

          } catch (error) {
            console.error('Error processing NFC reading:', error);
            this.setError(NFC_ERRORS.SCAN_FAILED, error.message, error);
          }
        };

        const errorHandler = (error) => {
          clearTimeout(timeoutId);
          console.error('NFC reading error:', error);
          this.setError(NFC_ERRORS.SCAN_FAILED, 'Failed to read NFC device', error);
        };

        ndef.addEventListener('reading', readingHandler);
        ndef.addEventListener('readingerror', errorHandler);

        return true;

      } catch (error) {
        if (error.name === 'NotAllowedError') {
          this.setError(NFC_ERRORS.PERMISSION_DENIED, 'NFC permission denied');
        } else {
          this.setError(NFC_ERRORS.SCAN_FAILED, error.message, error);
        }
        return false;
      }

    } catch (error) {
      console.error('Failed to start NFC scan:', error);

      if (error.message.includes('Rate limited') || error.message.includes('Too many attempts')) {
        this.setError(NFC_ERRORS.RATE_LIMITED, error.message);
      } else {
        this.setError(NFC_ERRORS.UNKNOWN_ERROR, error.message, error);
      }

      return false;
    }
  }

  // Validate detected NFC chip
  async _validateChip(serialNumber) {
    try {
      this.setState(NFC_STATES.VALIDATING_CHIP, {
        message: await nfcConfigManager.getText('validating')
      });

      // Initialize security manager if not done
      if (!nfcSecurityManager.isInitialized) {
        await nfcSecurityManager.initialize();
      }

      // Security validation with cryptographic checks
      await nfcSecurityManager.validateNFCChip(serialNumber);

      // Simulate validation delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Proceed to activation
      await this._processActivation(serialNumber);

    } catch (error) {
      console.error('Chip validation failed:', error);

      // Record security failure
      nfcSecurityManager.recordFailedAttempt(serialNumber, error.message);

      // Map security errors to user-friendly messages
      if (error.message.includes('not in authorized list')) {
        this.setError(NFC_ERRORS.INVALID_CHIP, await nfcConfigManager.getText('invalidChip'));
      } else if (error.message.includes('replay attack')) {
        this.setError(NFC_ERRORS.SCAN_FAILED, 'Security error: Please try again');
      } else if (error.message.includes('Rate limit')) {
        this.setError(NFC_ERRORS.RATE_LIMITED, 'Too many attempts. Please wait.');
      } else {
        this.setError(NFC_ERRORS.UNKNOWN_ERROR, error.message, error);
      }
    }
  }

  // Process NFC activation
  async _processActivation(nfcId) {
    try {
      this.setState(NFC_STATES.PROCESSING_ACTIVATION, {
        message: 'Processing activation...'
      });

      // Import license manager dynamically to avoid circular dependency
      const { default: licenseManager } = await import('./licenseManager.js');

      const result = await licenseManager.grantPremiumAccess(nfcId);

      if (result.success) {
        this.setState(NFC_STATES.SUCCESS, {
          message: result.message,
          type: result.type,
          nfcId: nfcId,
          expiresAt: result.expiresAt
        });
      } else {
        // Parse specific error types
        if (result.error.includes('already been claimed')) {
          this.setError(NFC_ERRORS.ALREADY_CLAIMED, result.error);
        } else if (result.error.includes('Must wait')) {
          this.setError(NFC_ERRORS.COOLDOWN_ACTIVE, result.error);
        } else if (result.error.includes('Server')) {
          this.setError(NFC_ERRORS.SERVER_ERROR, result.error);
        } else {
          this.setError(NFC_ERRORS.UNKNOWN_ERROR, result.error);
        }
      }

    } catch (error) {
      console.error('Activation processing failed:', error);

      if (error.message.includes('Network') || error.message.includes('fetch')) {
        this.setError(NFC_ERRORS.NETWORK_ERROR, 'Network connection failed');
      } else {
        this.setError(NFC_ERRORS.UNKNOWN_ERROR, error.message, error);
      }
    }
  }

  // Get user-friendly error message
  async getErrorMessage(errorType) {
    const config = await nfcConfigManager.getConfig();
    const texts = config.texts[nfcConfigManager.language] || config.texts.en;

    const errorMessages = {
      [NFC_ERRORS.NOT_SUPPORTED]: texts.notSupported,
      [NFC_ERRORS.PERMISSION_DENIED]: 'Permission to access NFC was denied',
      [NFC_ERRORS.INVALID_CHIP]: texts.invalidChip,
      [NFC_ERRORS.ALREADY_CLAIMED]: texts.alreadyClaimed,
      [NFC_ERRORS.COOLDOWN_ACTIVE]: texts.cooldownActive,
      [NFC_ERRORS.TIMEOUT]: texts.timeout,
      [NFC_ERRORS.RATE_LIMITED]: 'Too many scan attempts. Please wait before trying again.',
      [NFC_ERRORS.NETWORK_ERROR]: 'Network connection failed. Please check your internet connection.',
      [NFC_ERRORS.SERVER_ERROR]: 'Server error. Please try again later.',
      [NFC_ERRORS.USER_CANCELLED]: 'Scan cancelled by user',
      [NFC_ERRORS.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.'
    };

    return errorMessages[errorType] || errorMessages[NFC_ERRORS.UNKNOWN_ERROR];
  }

  // Get progress percentage for UI
  getProgress() {
    const progressMap = {
      [NFC_STATES.IDLE]: 0,
      [NFC_STATES.CHECKING_SUPPORT]: 10,
      [NFC_STATES.REQUESTING_PERMISSION]: 20,
      [NFC_STATES.INITIALIZING_SCAN]: 30,
      [NFC_STATES.SCANNING]: 40,
      [NFC_STATES.CHIP_DETECTED]: 60,
      [NFC_STATES.VALIDATING_CHIP]: 80,
      [NFC_STATES.PROCESSING_ACTIVATION]: 90,
      [NFC_STATES.SUCCESS]: 100,
      [NFC_STATES.ERROR]: 100,
      [NFC_STATES.TIMEOUT]: 100,
      [NFC_STATES.CANCELLED]: 100
    };

    return progressMap[this.state] || 0;
  }
}

export default new NFCStateManager();